import { useEffect, useState, type ReactNode } from 'react';
import { RefreshCw, Plus, Search, Download } from 'lucide-react';
import { searchCrm, exportCrmCsv, type CrmSearchResult } from '../../../api';
import type { CrmOpenDealHandler } from './crmDealDeepLink';
import type { AdminManagerBrief } from '../../../api';
import { CrmNewLeadModal } from './CrmNewLeadModal';
import { CrmInsightsStrip } from './CrmInsightsStrip';
import { SALES_STAGE_META } from './crmLabels';
import type { SalesStage } from '../../../api';
import { canCrmFullControl, canCrmManageLeads, type CrmAccessParams } from '../../../crmAccess';
import { useCrmPipeline, useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { CrmInlineError } from './CrmInlineError';

type Props = {
  access: CrmAccessParams;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onOpenDeal?: CrmOpenDealHandler;
  managerId?: number | null;
  crmDepartment?: string | null;
  showInsights?: boolean;
  managers?: AdminManagerBrief[];
  onNavigateMeasurement?: (id: number) => void;
  onNavigateClient360?: (userId: number) => void;
  onOpenMeasurementsForUser?: (userId: number) => void;
};

export function CrmPipelineKanban({
  access,
  hapticSelection,
  hapticSuccess,
  onOpenDeal,
  managerId,
  crmDepartment,
  showInsights,
  managers,
  onNavigateMeasurement: _onNavigateMeasurement,
  onNavigateClient360: _onNavigateClient360,
  onOpenMeasurementsForUser: _onOpenMeasurementsForUser,
}: Props) {
  const crmFull = canCrmFullControl(access);
  const canLeads = canCrmManageLeads(access);
  const [mineOnly, setMineOnly] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState<CrmSearchResult[]>([]);
  const invalidate = useInvalidateCrm();

  const effectiveManagerId = crmFull && !mineOnly ? (managerId ?? undefined) : undefined;
  const { data, isLoading, isError, error, refetch, isFetching } = useCrmPipeline({
    mineOnly: mineOnly || !crmFull,
    managerId: effectiveManagerId ?? null,
    includeUnassigned: true,
    crmDepartment: crmDepartment ?? null,
  });

  useEffect(() => {
    if (searchQ.trim().length < 2) {
      setSearchHits([]);
      return;
    }
    const t = setTimeout(() => {
      void searchCrm(searchQ.trim()).then((r) => setSearchHits(r.results));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const stages = data?.stages ?? (Object.keys(SALES_STAGE_META) as SalesStage[]);
  const leads = data?.leads ?? [];

  const openSearchHit = (hit: CrmSearchResult) => {
    hapticSelection();
    if (hit.type === 'order') {
      for (const cards of Object.values(data?.columns ?? {})) {
        const c = cards.find((x) => x.id === hit.id);
        if (c) {
          onOpenDeal?.({ kind: 'order', id: c.id }, { orderCard: c });
          setSearchQ('');
          setSearchHits([]);
          return;
        }
      }
    } else {
      const ld = leads.find((l) => l.id === hit.id);
      if (ld) {
        onOpenDeal?.({ kind: 'lead', id: ld.id }, { leadCard: ld });
        setSearchQ('');
        setSearchHits([]);
      }
    }
  };

  const onRefresh = () => {
    void refetch();
    void invalidate();
  };

  const downloadExport = async () => {
    hapticSelection();
    try {
      const csv = await exportCrmCsv({ manager_id: effectiveManagerId, days: 90 });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crm_export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-3">
      {showInsights && crmFull && <CrmInsightsStrip managerId={managerId} />}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[140px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--text-3)" />
          <input
            className="input-glass w-full text-sm pl-9 min-h-[44px] touch-manipulation"
            placeholder="Клиент, телефон, №…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
          {searchHits.length > 0 && (
            <div
              className="absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden max-h-48 overflow-y-auto"
              style={{ background: 'rgba(10,25,47,0.98)', border: '1px solid var(--border-subtle)' }}
            >
              {searchHits.map((h) => (
                <button
                  key={`${h.type}-${h.id}`}
                  type="button"
                  onClick={() => openSearchHit(h)}
                  className="press w-full text-left px-3 py-3 text-xs min-h-[44px] touch-manipulation"
                  style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-1)' }}
                >
                  <span className="font-bold">{h.label}</span>
                  <span className="block text-[10px]" style={{ color: 'var(--text-3)' }}>{h.subtitle}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {canLeads && (
          <button
            type="button"
            onClick={() => {
              hapticSelection();
              setShowNewLead(true);
            }}
            className="press p-2.5 rounded-lg shrink-0 min-h-[44px] min-w-[44px] touch-manipulation"
            style={{ background: 'rgba(72,187,120,0.2)', border: '1px solid rgba(72,187,120,0.35)' }}
            aria-label="Новый лид"
          >
            <Plus size={18} color="#68D391" />
          </button>
        )}
        {crmFull && (
          <button
            type="button"
            onClick={() => void downloadExport()}
            className="press p-2.5 rounded-lg shrink-0 min-h-[44px] touch-manipulation"
            style={{ background: 'rgba(255,255,255,0.06)' }}
            aria-label="Экспорт CSV"
          >
            <Download size={16} color="var(--cyan)" />
          </button>
        )}
        {crmFull && (
          <button
            type="button"
            onClick={() => {
              hapticSelection();
              setMineOnly((v) => !v);
            }}
            className="press px-3 py-2 rounded-lg text-[11px] font-bold shrink-0 min-h-[44px] touch-manipulation"
            style={
              mineOnly
                ? { background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }
                : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }
            }
          >
            {mineOnly ? 'Мои' : managerId ? 'Команда' : 'Все'}
          </button>
        )}
        <button
          type="button"
          onClick={onRefresh}
          className="press p-2.5 rounded-lg shrink-0 min-h-[44px] touch-manipulation"
          style={{ background: 'rgba(255,255,255,0.06)' }}
        >
          <RefreshCw size={16} color="var(--cyan)" className={isLoading || isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {isError && (
        <CrmInlineError
          message={formatAdminPanelError(error, 'Не удалось загрузить воронку')}
          onRetry={() => void refetch()}
        />
      )}

      <div
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory -mx-1 px-1"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {canLeads && (
          <KanbanColumn title="Лид" count={leads.length} accent="#68D391" bg="rgba(72,187,120,0.12)" border="rgba(72,187,120,0.3)">
            {leads.map((lead) => (
              <KanbanCard
                key={lead.id}
                title={lead.title}
                sub={`${lead.source}${lead.phone ? ` · ${lead.phone}` : ''}`}
                amount={null}
                badges={[
                  !lead.has_valid_open_task && !lead.has_overdue_task
                    ? { label: 'нет задачи', color: '#FC814A' }
                    : null,
                  lead.has_overdue_task ? { label: 'просрочено', color: '#FC814A' } : null,
                ].filter(Boolean) as { label: string; color: string }[]}
                onClick={() => {
                  hapticSelection();
                  onOpenDeal?.({ kind: 'lead', id: lead.id }, { leadCard: lead });
                }}
              />
            ))}
            {!isLoading && leads.length === 0 && <EmptyCol />}
          </KanbanColumn>
        )}

        {stages.filter((stage) => stage !== 'lost').map((stage) => {
          const meta = SALES_STAGE_META[stage];
          const cards = data?.columns[stage] ?? [];
          return (
            <KanbanColumn
              key={stage}
              title={meta.label}
              count={cards.length}
              accent={meta.color}
              bg={meta.bg}
              border={meta.border}
            >
              {cards.map((card) => {
                const mgr = card.assigned_manager_id
                  ? managers?.find((m) => m.id === card.assigned_manager_id)
                  : null;
                const mgrLabel = mgr
                  ? (mgr.first_name || mgr.username || `#${mgr.id}`).trim()
                  : null;
                const role = card.user.role;
                return (
                <KanbanCard
                  key={card.id}
                  title={card.user.first_name || card.user.username || `#${card.user.id}`}
                  sub={card.order_number}
                  amount={card.final_amount}
                  badges={[
                    role === 'partner' ? { label: 'Партнёр', color: '#63B3ED' } : null,
                    role === 'designer' ? { label: 'Дизайнер', color: '#F687B3' } : null,
                    mgrLabel ? { label: mgrLabel, color: '#63B3ED' } : null,
                    !card.has_open_task ? { label: 'нет задачи', color: '#FC814A' } : null,
                    card.has_overdue_task ? { label: 'просрочено', color: '#FC814A' } : null,
                    (card.measurement_photo_count ?? 0) > 0
                      ? { label: `📷 ${card.measurement_photo_count}`, color: '#68D391' }
                      : null,
                    card.has_measurement_report ? { label: 'отчёт', color: '#63B3ED' } : null,
                    card.delivery_address
                      ? { label: card.delivery_address.slice(0, 18) + (card.delivery_address.length > 18 ? '…' : ''), color: 'var(--text-3)' }
                      : null,
                    card.days_in_stage >= 3 ? { label: `${Math.round(card.days_in_stage)}д`, color: '#F6AD55' } : null,
                  ].filter(Boolean) as { label: string; color: string }[]}
                  onClick={() => {
                    hapticSelection();
                    onOpenDeal?.({ kind: 'order', id: card.id }, { orderCard: card });
                  }}
                />
              );
              })}
              {!isLoading && cards.length === 0 && <EmptyCol />}
            </KanbanColumn>
          );
        })}
      </div>

      {showNewLead && (
        <CrmNewLeadModal
          onClose={() => setShowNewLead(false)}
          onCreated={onRefresh}
          onOpenExisting={(id) => {
            onOpenDeal?.({ kind: 'lead', id });
          }}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
        />
      )}
    </div>
  );
}

function KanbanColumn({
  title,
  count,
  accent,
  bg,
  border,
  children,
}: {
  title: string;
  count: number;
  accent: string;
  bg: string;
  border: string;
  children: ReactNode;
}) {
  return (
    <div
      className="snap-start shrink-0 w-[min(78vw,260px)] flex flex-col rounded-2xl overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}
    >
      <div className="px-3 py-2 flex items-center justify-between sticky top-0 z-10" style={{ background: bg, borderBottom: `1px solid ${border}` }}>
        <span className="text-xs font-bold truncate" style={{ color: accent }}>{title}</span>
        <span className="text-sm font-black" style={{ color: accent }}>{count}</span>
      </div>
      <div className="flex-1 p-2 space-y-2 max-h-[58vh] overflow-y-auto overscroll-contain">{children}</div>
    </div>
  );
}

function KanbanCard({
  title,
  sub,
  amount,
  badges,
  onClick,
}: {
  title: string;
  sub: string;
  amount: number | null;
  badges: { label: string; color: string }[];
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="press w-full text-left p-3 rounded-xl touch-manipulation min-h-[44px]"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)' }}
    >
      <p className="text-xs font-bold truncate" style={{ color: 'var(--text-1)' }}>{title}</p>
      <p className="text-[10px] truncate" style={{ color: 'var(--text-3)' }}>{sub}</p>
      {amount != null && (
        <p className="text-sm font-bold mt-1" style={{ color: 'var(--cyan-bright)' }}>
          {Math.round(amount || 0).toLocaleString('ru-RU')} ₽
        </p>
      )}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {badges.map((b) => (
            <span key={b.label} className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${b.color}33`, color: b.color }}>
              {b.label}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function EmptyCol() {
  return (
    <p className="text-[10px] text-center py-4" style={{ color: 'var(--text-3)' }}>
      Пусто
    </p>
  );
}
